import React, { useState, useEffect } from 'react';
import {
  FileText,
  TrendingUp,
  RefreshCw,
  CreditCard,
  ShieldCheck,
  ArrowDownLeft,
  ArrowUpRight
} from 'lucide-react';
import { FinanceLedgerEntry } from '../../../types/admin.types';
import { fetchFinanceLedger } from '../../../lib/adminService';

interface FinanceLedgerTabProps {
  onShowNotice: (msg: string, type: 'success' | 'error' | 'info') => void;
}

export const FinanceLedgerTab: React.FC<FinanceLedgerTabProps> = ({ onShowNotice }) => {
  const [ledgerEntries, setLedgerEntries] = useState<FinanceLedgerEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadLedger = async () => {
    setIsLoading(true);
    try {
      const data = await fetchFinanceLedger();
      setLedgerEntries(data);
    } catch (err: any) {
      onShowNotice(err.message || 'Failed to load finance ledger', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLedger();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-black text-white tracking-tight">Immutable Finance Ledger</h2>
            <span className="px-2.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300 font-mono text-xs font-bold">
              Append-Only
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Section 22: Complete double-entry accounting records for client payments, platform fees, companion payouts, and refunds.
          </p>
        </div>

        <button
          type="button"
          onClick={loadLedger}
          className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 cursor-pointer self-start sm:self-auto"
          title="Refresh Ledger"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin text-purple-400' : ''}`} />
        </button>
      </div>

      {/* Ledger Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-950/60 text-slate-400 font-mono uppercase text-[10px] border-b border-slate-800">
              <tr>
                <th className="py-3 px-4">Entry ID</th>
                <th className="py-3 px-4">Movement Type</th>
                <th className="py-3 px-4">Reference (Booking / Payment)</th>
                <th className="py-3 px-4">Transaction Amount (₹)</th>
                <th className="py-3 px-4">Description</th>
                <th className="py-3 px-4 text-right">Timestamp</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60 text-slate-300">
              {ledgerEntries.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-500">
                    No ledger transactions recorded.
                  </td>
                </tr>
              ) : (
                ledgerEntries.map((e) => {
                  const isCredit = e.entryType === 'BOOKING_PAYMENT' || e.entryType === 'PLATFORM_FEE';
                  return (
                    <tr key={e.id} className="hover:bg-slate-800/40 transition-colors">
                      <td className="py-3.5 px-4 font-mono font-bold text-purple-400">{e.id}</td>
                      <td className="py-3.5 px-4">
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold font-mono border inline-flex items-center gap-1 ${
                            e.entryType === 'PLATFORM_FEE'
                              ? 'bg-purple-500/15 text-purple-300 border-purple-500/30'
                              : e.entryType === 'BOOKING_PAYMENT'
                              ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                              : e.entryType === 'COMPANION_PAYOUT'
                              ? 'bg-blue-500/15 text-blue-300 border-blue-500/30'
                              : 'bg-rose-500/15 text-rose-400 border-rose-500/30'
                          }`}
                        >
                          {e.entryType}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 font-mono text-slate-400">
                        {e.bookingId || e.paymentId || '—'}
                      </td>
                      <td className="py-3.5 px-4 font-mono font-bold">
                        <span className={isCredit ? 'text-emerald-400' : 'text-purple-300'}>
                          {isCredit ? '+' : '-'}₹{e.amount.toLocaleString('en-IN')}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-slate-300">{e.description}</td>
                      <td className="py-3.5 px-4 text-right text-slate-400 font-mono text-[11px]">
                        {new Date(e.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
